import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import useRecordPagination from '../../shared/useRecordPagination';
import { Form, Select, FormLayout, Layout, Page, Card, TextField, Checkbox, BlockStack, InlineGrid, Badge, Banner, Button, Box, Text } from '@shopify/polaris';
import CompanySelect from '../../shared/components/companySelect';
import LinesTable from '../../shared/components/linesTable';
import { TheSaveBar } from '../../shared/components/theSaveBar';
import { updateMyData } from '../../shared/util/updateLines';
import { handleSave, handleDiscard, useUrl } from '../../shared/util/hanldesave';
import { alphabetize, getRequest, simplePostRequest } from '../../shared/components/functions.jsx';
import DocumentCard from '../../shared/components/cards/DocumentCard';
import { useAuthStore, useLookupStore } from '../../context/useStore.js';
import ProjectSelect from '../../shared/components/projectSelect';
import { LinesTotalTable } from '../../shared/components/cards/LinesTotalsCard.jsx';
import { validateLines, validateSchema } from '../../helpers/schemas/schemasHelpers.js';
import Toasts from '../../shared/components/toasts/toasts.jsx';
import { getPageTitle, TabTitle } from '../../shell/helmet';
import { getSchema } from '../../shared/formSchemas';
import EventTimeLine from '../../shared/components/events/eventTimeLine';
import { CardHeading } from '../../shared/components/cards/CardHeading';
import OwnerSelect from '../../shared/components/ownerSelect.jsx';
import PurchaseReceivingPanel from './components/PurchaseReceivingPanel.jsx';
import PurchaseConfirmationPanel from './components/PurchaseConfirmationPanel.jsx';
import DuplicatePurchaseModal from './components/DuplicatePurchaseModal.jsx';
import InquiryDocumentCopyBanner from './components/InquiryDocumentCopyBanner.jsx';
import { useFillColumnHeight } from './useFillColumnHeight.js';
import { usePartDocumentsPrompt } from '../../shared/components/modals/partDocumentsPromptModal.jsx';

const PURCHASE_STATUS_IDS = {
  DRAFT: 1,
  SENT: 2,
  PARTIALLY_RECEIVED: 3,
  RECEIVED: 4,
  CANCELLED: 5
};

// one tabbed card next to the form instead of a column of cards that pushed the lines table down.
// Not Polaris Tabs: in this narrow column (sidebar open, laptop screen) those fold tabs into a "More views" menu, this bar wraps instead
const SIDE_TABS = [
  { id: 'purchase-document', content: 'Document' },
  { id: 'purchase-confirmation', content: 'Confirmation' },
  { id: 'purchase-receiving', content: 'Receiving' },
  { id: 'purchase-timeline', content: 'Timeline' }
];
const TIMELINE_TAB_INDEX = 3;

const ALWAYS_EDITABLE_DATE_FIELDS = new Set(['DeliveryDate', 'SupplierConfirmedDeliveryDate', 'RefKlantDatum']);

// A purchase order PDF can be generated without prices and/or without the end customer. The choice is not stored on the
// purchase itself but read back from the description of the generated system document (see server/routes/docgen.js)
const PRICES_HIDDEN_MARKER = 'prices hidden';
const CUSTOMER_HIDDEN_MARKER = 'customer hidden';

function deriveStatusId(purchase = {}) {
  if (purchase.PurchaseStatusID) return parseInt(purchase.PurchaseStatusID);
  if (purchase.Geannuleerd) return PURCHASE_STATUS_IDS.CANCELLED;
  if (purchase.Ontvangen) return PURCHASE_STATUS_IDS.RECEIVED;
  if (purchase.Verstuurd) return PURCHASE_STATUS_IDS.SENT;
  return PURCHASE_STATUS_IDS.DRAFT;
}

export default function PurchasePage() {
  let { id } = useParams();
  const pagination = useRecordPagination('purchases', id, 'purchase');
  const navigate = useNavigate();
  let url = useUrl();
  const docTypeID = 13;
  const docType = 'order';
  const location = useLocation();
  const [savingResult, setSavingResult] = useState(false);
  const [purchase, setPurchase] = useState({});
  const [initialPurchase, setInitialPurchase] = useState({});
  const [lines, setLines] = useState([]);
  const [initialLines, setInitialLines] = useState([]);
  const [toast, setToast] = useState(false);
  const [toastError, setToastError] = useState(false); //eslint-disable-line
  const [isDirty, setIsDirty] = useState(url.get('isDirty') || false);
  const [linesDirty, setLinesDirty] = useState(url.get('linesDirty') || false);
  const [company, setCompany] = useState({});
  const [customer, setCustomer] = useState({});
  const [deliveryCompany, setDeliveryCompany] = useState({});
  const [isBlurred, setIsBlurred] = useState(true);
  const [documentInProgress, setDocumentInProgress] = useState(false);
  const [hidePrices, setHidePrices] = useState(false);
  const [hideCustomer, setHideCustomer] = useState(false);
  const [purchaseStatuses, setPurchaseStatuses] = useState([]);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  // inquiry files picked in "Make purchase order" (inquiry page), copied once this new order is saved
  const [inquiryDocumentCopy, setInquiryDocumentCopy] = useState(() => {
    const inquiryId = parseInt(url.get('copyFromInquiryID'));
    const documentIds = String(url.get('copyDocumentIDs') || '')
      .split(',')
      .map((value) => parseInt(value))
      .filter((value) => Number.isInteger(value));
    return id === 'new' && inquiryId && documentIds.length ? { inquiryId, documentIds } : null;
  });
  const [documentCardKey, setDocumentCardKey] = useState(0);
  const [selectedSideTab, setSelectedSideTab] = useState(0);
  const [timelineRefresh, setTimelineRefresh] = useState(0);
  // parts with documents (drawings, ...) put on a line: offer to copy those files into this order
  const partDocuments = usePartDocumentsPrompt({
    resourceName: 'purchases',
    resourceId: id,
    docTypeId: docTypeID,
    label: 'purchase order',
    onCopied: () => setDocumentCardKey((key) => key + 1)
  });
  const { countries, languages, purchasetypes } = useLookupStore();
  const { user } = useAuthStore();
  const pageTitle = getPageTitle(purchase.Bestellingnummer, 'Purchase');
  const purchaseSchema = getSchema('purchase', url, null, user);
  const effectiveStatusId = deriveStatusId(purchase);
  const isLockedForEditing = effectiveStatusId === PURCHASE_STATUS_IDS.PARTIALLY_RECEIVED || effectiveStatusId === PURCHASE_STATUS_IDS.RECEIVED || effectiveStatusId === PURCHASE_STATUS_IDS.CANCELLED;

  const getNormalizedFieldValue = (field, value) => {
    if (ALWAYS_EDITABLE_DATE_FIELDS.has(field)) return value ? String(value).substring(0, 10) : null;
    return value ?? null;
  };

  const dirtyPurchaseFields = Object.keys(purchase || {}).filter((field) => getNormalizedFieldValue(field, purchase?.[field]) !== getNormalizedFieldValue(field, initialPurchase?.[field]));
  const canSaveLockedDateFields = dirtyPurchaseFields.length > 0 && dirtyPurchaseFields.every((field) => ALWAYS_EDITABLE_DATE_FIELDS.has(field));

  const lineInterface = {
    lineIsDirty: false,
    BestellingLijnID: null,
    BestellingID: id,
    produktnr: null,
    produkthulpnr: null,
    tekstlijn: null,
    VP: null,
    aantal: 1,
    BTW: company.LandID === 300 ? 21 : null,
    MuntVP: null,
    PrijsaanvraagID: null,
    AP: null,
    ReceivedQty: 0,
    ProductID: null,
    ProductName: null,
    ProductNameNL: '',
    ProductNameFR: '',
    Productref: '',
    SupplierID: null,
    UnitsOnOrder: null,
    'Eenheids verkoopprijs': null,
    'Munt verkoopprijs': null,
    'Datum  bijw_ VP': '',
    ReorderLevel: 0,
    Discontinued: false,
    Eenheidsaankoopprijs: 0,
    'Munt aankoopprijs': null,
    'Datum bijw_AP': '',
    Omschrijving: '',
    Eenheid: null,
    ProductNameDE: '',
    ProductNameUK: '',
    EenheidID: null,
    EenheidAantal: null,
    ParentProductID: null,
    HasDetails: false,
    HSCode: null,
    Kg: null,
    OnderdelenTypeID: null
  };

  const handleChange = async (value, id) => {
    if (id === 'RefKlantDatum' && (!value || value === '')) value = null;

    if (id === 'DeliveryDate') {
      setPurchase((purchase) => ({ ...purchase, DeliveryDate: value || null }));
      setIsDirty(true);
      return;
    }

    // If a project is selected, automatically set or update the reference to PROJECT-XXXXXX format
    if (id === 'ProjectID') {
      try {
        if (value) {
          // Project is selected - fetch project data and update reference
          const projectData = await getRequest(`/api/projects/${value}`);
          if (projectData && projectData[0] && projectData[0].Projectnummer) {
            const newReference = `PROJECT-${projectData[0].Projectnummer}`;

            // Check if current reference is empty or still follows the old project format
            const shouldUpdateReference = !purchase.RefKlant || purchase.RefKlant.startsWith('PROJECT-') || purchase.RefKlant === '';

            if (shouldUpdateReference) {
              setPurchase((purchase) => ({
                ...purchase,
                [id]: value,
                RefKlant: newReference,
                KlantID: projectData[0].firnr || purchase.KlantID, // Set customer from project
                Aanbod: newReference // Set aanbod/angebot to the same value as reference
              }));
              setIsDirty(true);
              return;
            } else {
              // Even if reference shouldn't update, still set the customer and aanbod
              setPurchase((purchase) => ({
                ...purchase,
                [id]: value,
                KlantID: projectData[0].firnr || purchase.KlantID,
                Aanbod: newReference // Set aanbod/angebot to the same value as reference
              }));
              setIsDirty(true);
              return;
            }
          }
        } else {
          // Project is cleared - clear the reference if it was in PROJECT-XXXXXX format
          if (purchase.RefKlant && purchase.RefKlant.startsWith('PROJECT-')) {
            setPurchase((purchase) => ({
              ...purchase,
              [id]: value,
              RefKlant: ''
            }));
            setIsDirty(true);
            return;
          }
        }
      } catch (error) {
        console.error('Error fetching project data:', error);
      }
    }

    setPurchase((purchase) => ({ ...purchase, [id]: value }), setIsDirty(true));
  };

  const reloadPurchaseAndLines = async (purchaseId = id) => {
    if (purchaseId === 'new') return;
    const data = await getRequest(`/api/purchases/${purchaseId}`);
    if (data) {
      setPurchase(data);
      setInitialPurchase(data);
    }
    const data2 = await getRequest(`/api/purchases/${purchaseId}/lines`);
    if (Array.isArray(data2)) {
      setLines(data2);
      setInitialLines(data2);
    }
  };

  const loadHidePricesFromDocument = async (purchaseId) => {
    const documents = await getRequest(`/api/documents/id/typeid?id=${purchaseId}&typeID=${docTypeID}&sysDoc=1`);
    const description = Array.isArray(documents) && documents.length > 0 ? String(documents[0].Omschrijving || '') : '';
    setHidePrices(description.toLowerCase().includes(PRICES_HIDDEN_MARKER));
    setHideCustomer(description.toLowerCase().includes(CUSTOMER_HIDDEN_MARKER));
  };

  useEffect(() => {
    async function fetchPurchaseData() {
      if (id === 'new') {
        setPurchase(purchaseSchema.default());
        setInitialPurchase(purchaseSchema.default());
        setLines([]);
        setInitialLines([]);
        setHidePrices(false);
        setHideCustomer(false);
      } else {
        await reloadPurchaseAndLines(id);
        await loadHidePricesFromDocument(id);
      }
    }
    fetchPurchaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    async function fetchLookups() {
      const statuses = await getRequest('/api/purchases/statuses');
      if (Array.isArray(statuses)) setPurchaseStatuses(statuses);
    }
    fetchLookups();
  }, []);

  useEffect(() => {
    async function fetchData() {
      if (purchase.LeverancierID) {
        const data3 = await getRequest(`/api/companies/${purchase.LeverancierID}`);
        setCompany(data3);
      }
      if (purchase.KlantID) {
        const data4 = await getRequest(`/api/companies/${purchase.KlantID}`);
        setCustomer(data4);
      }
    }
    fetchData();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchase.LeverancierID, purchase.KlantID]);

  useEffect(() => {
    async function fetchDeliveryCompany() {
      setDeliveryCompany(purchase.LeveringKlantID ? (await getRequest(`/api/companies/${purchase.LeveringKlantID}`)) || {} : {});
    }
    fetchDeliveryCompany();
  }, [purchase.LeveringKlantID]);

  useEffect(() => {
    if (location.state) setLines(location.state);
  }, [location]);

  const generateDocument = async () => {
    const document = {
      addressee: {
        FIRNR: purchase.LeverancierID,
        name: company.NAAM,
        number: company.Klantnummer,
        address: company.STRAAT_NR,
        city: company.WOONPL,
        zip: company.POSTNR,
        country: countries.find((c) => c.LandId === company.LandID)?.Land,
        countryId: company.LandID,
        vat: company.BTW,
        postal_code: company.postkode
      },
      eu: false,
      language: purchase.TaalID ? languages.find((l) => l.TaalID === parseInt(purchase.TaalID))?.Taal : 'N',
      items: lines,
      type: docType, //{'invoice','service_report','delivery_note','quotation','service_report','order_confirmation','maintenance_contract','order','price_request','reminder','project','credit_note','proforma','invoice_in','credit_note_in','letter','fax','info_schuilenburg','part','customer'}
      typeID: docTypeID,
      id: purchase.BestellingID,
      number: purchase.Bestellingnummer,
      reference: customer.NAAM,
      reference_date: purchase.RefKlantDatum ? String(purchase.RefKlantDatum).substring(0, 10) : '',
      date: String(purchase.Datum).substring(0, 10),
      payment_condition: null,
      delivery_term: purchase.DeliveryDate ? String(purchase.DeliveryDate).substring(0, 10) : '-',
      aanbod: purchase.Aanbod,
      machine_type: purchase.MachineType,
      machine_number: purchase.MachineNummer,
      hidePrices,
      hideCustomer,
      // optional delivery address (a customer or another supplier), printed on the PDF so the supplier knows where to deliver
      deliveryAddressee:
        purchase.LeveringKlantID && String(deliveryCompany.FIRNR) === String(purchase.LeveringKlantID)
          ? {
              name: deliveryCompany.NAAM,
              number: deliveryCompany.Klantnummer,
              address: deliveryCompany.STRAAT_NR,
              city: deliveryCompany.WOONPL,
              zip: deliveryCompany.POSTNR,
              country: countries.find((c) => c.LandId === deliveryCompany.LandID)?.Land,
              countryId: deliveryCompany.LandID
            }
          : null
    };

    //check if it is dirty if not tell user to save first
    setDocumentInProgress(true);
    const res = await simplePostRequest(`/api/docgen`, document);
    if (res.ok) setDocumentInProgress(false);
  };

  const handlePurchaseConfirmationUpdated = (updatedPurchase) => {
    if (!updatedPurchase) return;
    setPurchase(updatedPurchase);
    setInitialPurchase(updatedPurchase);
    setIsDirty(false);
  };

  async function saveForm() {
    if (isLockedForEditing && !canSaveLockedDateFields) {
      alert('This purchase order is locked. Use the receiving section to register deliveries.');
      return;
    }

    const errorInSchema = await validateSchema(purchaseSchema, purchase);
    const errorInLines = await validateLines(lines);
    if (errorInLines) return alert(errorInLines);

    if (!documentInProgress) {
      if (!errorInSchema) {
        await handleSave(
          id,
          'purchases',
          purchase.BestellingID,
          purchase,
          setPurchase,
          setInitialPurchase,
          setToast,
          setToastError,
          isDirty,
          setIsDirty,
          linesDirty,
          lines,
          setLines,
          setInitialLines,
          setLinesDirty,
          setSavingResult,
          navigate
        );

        // auto generate pdf so latest changes are reflected in pdf
        if (id && id !== 'new') await generateDocument();
        setIsBlurred(false);
      } else {
        alert(errorInSchema);
      }
    }
  }

  const markAsSent = async () => {
    if (id === 'new') return;
    setSavingResult(true);
    try {
      const response = await fetch(`/api/purchases/${id}/mark-sent`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) {
        alert(data?.msg || 'Failed to mark purchase as sent.');
        return;
      }
      setPurchase(data);
      setInitialPurchase(data);
      setIsDirty(false);
      setLinesDirty(false);
      setToast(true);
    } catch (err) {
      alert('Failed to mark purchase as sent.');
    } finally {
      setSavingResult(false);
    }
  };

  const statusLabel = purchaseStatuses.find((status) => String(status.PurchaseStatusID) === String(effectiveStatusId))?.Status || purchase.PurchaseStatusName || 'Draft';
  const statusTone =
    effectiveStatusId === PURCHASE_STATUS_IDS.RECEIVED
      ? 'success'
      : effectiveStatusId === PURCHASE_STATUS_IDS.PARTIALLY_RECEIVED
      ? 'attention'
      : effectiveStatusId === PURCHASE_STATUS_IDS.SENT
      ? 'info'
      : effectiveStatusId === PURCHASE_STATUS_IDS.CANCELLED
      ? 'critical'
      : undefined;
  const formDisabled = savingResult || isLockedForEditing;
  const dateFieldDisabled = savingResult;

  const handleSideTabSelect = (index) => {
    setSelectedSideTab(index);
    // the panels stay mounted, so refetch the timeline to show what changed since the page was opened
    if (index === TIMELINE_TAB_INDEX) setTimelineRefresh((token) => token + 1);
  };
  const sideTabPanelStyle = (index) => (selectedSideTab === index ? undefined : { display: 'none' });
  // the attached files list grows so the side card is as tall as the purchase details next to it
  const documentsFill = useFillColumnHeight({ defaultHeight: 180, blockSelector: '[data-file-list-container]', enabled: selectedSideTab === 0 && id !== 'new' });

  return (
    <Page
      pagination={pagination}
      title={pageTitle}
      fullWidth
      secondaryActions={[{ content: 'Duplicate', onAction: () => setDuplicateModalOpen(true), disabled: id === 'new' || isDirty || linesDirty || savingResult }]}
    >
      <TabTitle title={pageTitle} />
      <DuplicatePurchaseModal
        open={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        purchaseId={id}
        purchaseNumber={purchase.Bestellingnummer}
        onDuplicated={(data) => {
          setDuplicateModalOpen(false);
          navigate(`/purchases/${data.purchase.BestellingID}`);
        }}
      />
      <Toasts toast={toast} setToast={setToast} errorToast={toastError} setErrorToast={setToastError} toastContent="Purchase saved" />
      <TheSaveBar
        isDirty={isDirty || linesDirty}
        savingResult={savingResult}
        handleDiscard={() => handleDiscard(initialPurchase, initialLines, setPurchase, setLines, setIsDirty, setLinesDirty)}
        handleSave={saveForm}
        disabled={isLockedForEditing || documentInProgress || (!isBlurred && linesDirty)}
      />
      <Layout>
        <Layout.Section>
          <span ref={documentsFill.mainColumnMarkerRef} hidden />
          {inquiryDocumentCopy ? (
            <div style={{ marginBottom: '16px' }}>
              <InquiryDocumentCopyBanner purchaseId={id} request={inquiryDocumentCopy} onFinished={() => setDocumentCardKey((key) => key + 1)} onDismiss={() => setInquiryDocumentCopy(null)} />
            </div>
          ) : null}
          {isLockedForEditing && (
            <div style={{ marginBottom: '16px' }}>
              <Banner tone="info" title="Purchase is locked">
                This purchase order is locked for most edits. Delivery dates remain editable, and the receiving section is still used to register partial or full deliveries.
              </Banner>
            </div>
          )}
          <Card>
            <Form onSubmit={saveForm} autoComplete="off">
              <FormLayout>
                <FormLayout.Group>
                  <TextField id="Bestellingnummer" disabled label="Number" value={purchase.Bestellingnummer ? String(purchase.Bestellingnummer) : 'created automatically'} />
                  <TextField id="LeverancierID" label="Supplier" value={company.NAAM} disabled labelAction={{ content: 'open', onAction: () => navigate(`/companies/${purchase.LeverancierID}`) }} />
                  <div>
                    <div style={{ marginBottom: '4px', fontSize: '12px', color: '#6d7175' }}>Status</div>
                    <Badge tone={statusTone}>{statusLabel}</Badge>
                  </div>
                  {effectiveStatusId === PURCHASE_STATUS_IDS.DRAFT && id !== 'new' ? (
                    <div style={{ display: 'flex', alignItems: 'end' }}>
                      <Button onClick={markAsSent} disabled={savingResult}>
                        Mark as sent
                      </Button>
                    </div>
                  ) : null}
                </FormLayout.Group>
                <FormLayout.Group>
                  <TextField id="Datum" maxLength={50} type="date" autoComplete="off" label="Date" value={String(purchase.Datum).substring(0, 10)} onChange={handleChange} disabled={formDisabled} />
                  <CompanySelect isClearable id="KlantID" label="Customer" value={purchase.KlantID} onChange={handleChange} disabled={formDisabled} />
                </FormLayout.Group>
                <CompanySelect
                  isClearable
                  id="LeveringKlantID"
                  label="Delivery address (optional, printed on the PDF)"
                  placeholder="Our own address"
                  value={purchase.LeveringKlantID}
                  onChange={handleChange}
                  isDisabled={formDisabled}
                />
                <FormLayout.Group>
                  <Select
                    id="TaalID"
                    maxLength={50}
                    autoComplete="off"
                    label="Language"
                    value={parseInt(purchase.TaalID) || ''}
                    onChange={handleChange}
                    disabled={formDisabled}
                    options={languages.sort((a, b) => alphabetize(a, b, 'Omschrijving')).map((lan) => ({ value: lan.TaalID, label: lan.Omschrijving }))}
                  />
                  <TextField id="RefKlant" maxLength={100} type="text" autoComplete="off" label="Reference customer" value={purchase.RefKlant || ''} onChange={handleChange} disabled={formDisabled} />
                </FormLayout.Group>
                <FormLayout.Group>
                  <TextField
                    id="DeliveryDate"
                    type="date"
                    autoComplete="off"
                    label="Delivery date"
                    value={purchase.DeliveryDate ? String(purchase.DeliveryDate).substring(0, 10) : ''}
                    onChange={handleChange}
                    disabled={dateFieldDisabled}
                  />
                  <TextField
                    id="RefKlantDatum"
                    maxLength={50}
                    type="date"
                    autoComplete="off"
                    label="Customer date"
                    value={String(purchase.RefKlantDatum).substring(0, 10)}
                    onChange={handleChange}
                    disabled={dateFieldDisabled}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <ProjectSelect id="ProjectID" label="Project" value={purchase.ProjectID} onChange={handleChange} isDisabled={formDisabled} />
                  <TextField id="MachineType" maxLength={50} autoComplete="off" label="Machine type" value={purchase.MachineType || ''} onChange={handleChange} disabled={formDisabled} />
                </FormLayout.Group>
                <FormLayout.Group>
                  <Select
                    id="BestellingTypeID"
                    label="Purchase type"
                    value={parseInt(purchase.BestellingTypeID) || ''}
                    onChange={handleChange}
                    disabled={formDisabled}
                    options={purchasetypes.sort((a, b) => alphabetize(a, b, 'BestellingType')).map((pt) => ({ value: pt.BestellingTypeID, label: pt.BestellingType }))}
                  />
                  <TextField id="MachineNummer" maxLength={50} autoComplete="off" label="Machine number" value={purchase.MachineNummer || ''} onChange={handleChange} disabled={formDisabled} />
                </FormLayout.Group>
                <FormLayout.Group>
                  <InlineGrid columns={2}>
                    <BlockStack>
                      <Checkbox id="Verstuurd" label="PDF Document emailed" checked={purchase.Verstuurd} onChange={handleChange} disabled={formDisabled} />
                      <Checkbox id="Geannuleerd" label="Cancelled" checked={purchase.Geannuleerd} onChange={handleChange} disabled={formDisabled} />
                    </BlockStack>
                    <OwnerSelect id="GebruikerID" label="Owner" value={purchase.GebruikerID} handleChange={handleChange} disabled={formDisabled} />
                  </InlineGrid>
                  <TextField id="Aanbod" maxLength={50} autoComplete="off" label="Aanbod" value={purchase.Aanbod || ''} onChange={handleChange} disabled={formDisabled} />
                </FormLayout.Group>
                {/* not part of generateDocument, so it never reaches the supplier */}
                <TextField
                  id="InternalNote"
                  maxLength={1000}
                  multiline={2}
                  autoComplete="off"
                  label="Internal note"
                  helpText="Only visible in the admin, not printed on the purchase order. Use it for internal references, e.g. on small orders without a project."
                  value={purchase.InternalNote || ''}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              </FormLayout>
            </Form>
          </Card>
        </Layout.Section>
        <Layout.Section variant="oneThird">
          <span ref={documentsFill.sideColumnMarkerRef} hidden />
          <Card>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {SIDE_TABS.map((tab, index) => (
                <div key={tab.id} style={{ flex: '1 0 auto' }}>
                  <Button id={tab.id} variant="tertiary" fullWidth pressed={selectedSideTab === index} onClick={() => handleSideTabSelect(index)}>
                    {tab.content}
                  </Button>
                </div>
              ))}
            </div>
            {/* every panel stays mounted (hidden when not selected) so uploads in progress and fetched file lists survive a tab switch */}
            <Box paddingBlockStart="400">
              <div style={sideTabPanelStyle(0)}>
                <DocumentCard
                  key={documentCardKey}
                  embedded
                  resource="PURCHASE"
                  id={id}
                  title="Purchase document"
                  docNumber={purchase.Bestellingnummer}
                  isDirty={isDirty}
                  linesDirty={linesDirty}
                  generateDocument={generateDocument}
                  docTypeID={docTypeID}
                  docType={docType}
                  company={company}
                  setResource={setPurchase}
                  docLang={purchase.TaalID}
                  fileBrowserHeight={`${documentsFill.height}px`}
                  documentButtonLoading={savingResult || documentInProgress}
                  setDocumentButtonLoading={setSavingResult}
                  documentOptions={
                    <BlockStack gap="100">
                      <Checkbox
                        label="Hide prices on PDF"
                        helpText="Applies to the next generated PDF"
                        checked={hidePrices}
                        onChange={(checked) => setHidePrices(checked)}
                        disabled={savingResult || documentInProgress}
                      />
                      <Checkbox
                        label="Hide customer on PDF"
                        helpText="Leaves the end customer off the next generated PDF"
                        checked={hideCustomer}
                        onChange={(checked) => setHideCustomer(checked)}
                        disabled={savingResult || documentInProgress}
                      />
                    </BlockStack>
                  }
                />
              </div>
              <div style={sideTabPanelStyle(1)}>
                <PurchaseConfirmationPanel
                  purchaseId={id}
                  purchase={purchase}
                  company={company}
                  onPurchaseUpdated={handlePurchaseConfirmationUpdated}
                  onFieldChange={handleChange}
                  fieldDisabled={dateFieldDisabled}
                  uploadDisabled={savingResult}
                />
              </div>
              <div style={sideTabPanelStyle(2)}>
                {id !== 'new' ? (
                  <PurchaseReceivingPanel
                    purchaseId={id}
                    purchase={purchase}
                    company={company}
                    onReceiptCreated={async () => {
                      await reloadPurchaseAndLines(id);
                      setIsDirty(false);
                      setLinesDirty(false);
                    }}
                  />
                ) : (
                  <Text tone="subdued">Receiving becomes available once the purchase order has been saved.</Text>
                )}
              </div>
              <div style={sideTabPanelStyle(TIMELINE_TAB_INDEX)}>
                <EventTimeLine embedded resourceName="PURCHASE" resourceId={id} refreshToken={timelineRefresh} />
              </div>
            </Box>
          </Card>
        </Layout.Section>
        <Layout.Section>
          <Card>
            <CardHeading title="Purchase content" />
            {partDocuments.modal}
            {partDocuments.banner ? <div style={{ marginBottom: '12px' }}>{partDocuments.banner}</div> : null}
            <LinesTable
              id={id}
              data={lines}
              updateMyData={updateMyData}
              setLines={setLines}
              setLinesDirty={setLinesDirty}
              onSubmit={saveForm}
              lineInterface={lineInterface}
              resource={purchase}
              company={company}
              disabled={formDisabled}
              setIsBlurred={setIsBlurred}
              enableAiTextProcessing={true}
              onProductsAdded={partDocuments.promptForProducts}
            />
            <Box paddingBlockStart="400">
              <div style={{ marginLeft: 'auto', maxWidth: '400px' }}>
                <LinesTotalTable summary="purchase" lines={lines} priceField="AP" />
              </div>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
